import React, { useEffect, useState } from 'react';
import { Building2, Save, Plus, Trash2, RefreshCw, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select } from '@/components/admin/shared';

const PLATFORM_OPTS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter',   label: 'Twitter / X' },
  { value: 'youtube',   label: 'YouTube' },
  { value: 'spotify',   label: 'Spotify' },
  { value: 'tiktok',    label: 'TikTok' },
  { value: 'linkedin',  label: 'LinkedIn' },
  { value: 'facebook',  label: 'Facebook' },
  { value: 'custom',    label: 'Other' },
];

export default function CompanyInfoManager() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/company-info');
      setForm({ ...data, social_links: data.social_links || [] });
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to load company info'); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const setSocial = (i, k, v) => setForm((f) => {
    const arr = [...(f.social_links || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...f, social_links: arr };
  });
  const addSocial = () => setForm((f) => ({ ...f, social_links: [...(f.social_links || []), { platform: 'instagram', label: 'Instagram', url: '', enabled: true }] }));
  const removeSocial = (i) => setForm((f) => ({ ...f, social_links: (f.social_links || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.updated_at;
      payload.copyright_start_year = Number(payload.copyright_start_year) || new Date().getFullYear();
      const { data } = await api.put('/admin/company-info', payload);
      setForm({ ...data, social_links: data.social_links || [] });
      setMsg('Company info saved.');
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]">Loading company info…</div>;

  return (
    <div className="space-y-6" data-testid="company-info-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Company Information</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><RefreshCw size={14} /> Reload</button>
          <button data-testid="company-info-save" onClick={save} disabled={saving} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {msg && (
        <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)] flex items-center gap-2">
          <Check size={14} className="text-[var(--pg-emerald-2)]" /> {msg}
        </div>
      )}

      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px]">Identity</h4>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Business name (legal)" value={form.business_name} onChange={set('business_name')} />
          <TextField label="Brand name (display)" value={form.brand_name} onChange={set('brand_name')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Copyright start year" type="number" value={form.copyright_start_year} onChange={set('copyright_start_year')} />
          <TextField label="Website URL" value={form.website_url} onChange={set('website_url')} placeholder="https://…" />
        </div>
      </section>

      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px]">Contact</h4>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Contact email" value={form.contact_email} onChange={set('contact_email')} placeholder="hello@…" />
          <TextField label="Support email" value={form.support_email} onChange={set('support_email')} placeholder="support@…" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Business phone" value={form.business_phone} onChange={set('business_phone')} />
          <TextField label="Business hours" value={form.business_hours} onChange={set('business_hours')} placeholder="Mon–Fri 9–5 EST" />
        </div>
        <TextField label="Mailing address" value={form.mailing_address} onChange={set('mailing_address')} textarea rows={2} />
        <TextField label="Footer text (short line under links)" value={form.footer_text} onChange={set('footer_text')} placeholder="a channel for the curious." />
      </section>

      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="pg-label text-[10px]">Social Links</h4>
          <button data-testid="company-social-add" onClick={addSocial} className="pg-btn-ghost px-2 py-1 rounded-[var(--pg-radius-sm)] text-[11px] flex items-center gap-1">
            <Plus size={12} /> Add link
          </button>
        </div>
        {(form.social_links || []).length === 0 && (
          <p className="text-[11px] font-mono text-[var(--pg-ink-faint)]">No social links yet. Add one to display in the footer.</p>
        )}
        {(form.social_links || []).map((s, i) => (
          <div key={i} className="pg-input rounded-[var(--pg-radius-sm)] p-3 grid grid-cols-12 gap-2 items-center">
            <div className="col-span-3">
              <Select label="Platform" value={s.platform || 'custom'} onChange={(v) => setSocial(i, 'platform', v)} options={PLATFORM_OPTS} />
            </div>
            <div className="col-span-3">
              <TextField label="Label" value={s.label} onChange={(v) => setSocial(i, 'label', v)} />
            </div>
            <div className="col-span-4">
              <TextField label="URL" value={s.url} onChange={(v) => setSocial(i, 'url', v)} placeholder="https://…" />
            </div>
            <div className="col-span-1">
              <Toggle label="On" value={s.enabled !== false} onChange={(v) => setSocial(i, 'enabled', v)} />
            </div>
            <div className="col-span-1 flex justify-end">
              <button onClick={() => removeSocial(i)} className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-neon-pink)]" aria-label="remove">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </section>

      <div className="flex justify-end">
        <button data-testid="company-info-save-bottom" onClick={save} disabled={saving} className="pg-btn-primary px-4 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
          <Save size={14} /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
